import React from 'react'
import { useOutletContext } from 'react-router-dom';

import { useTheme, createTheme, Box, IconButton, Tooltip,
  FormControl, Select as MuiSelect, MenuItem, Snackbar, Alert,
  Card, CardContent, Slider, FormControlLabel, 
  RadioGroup, Radio, Button, Typography } from '@mui/material'
import { Fullscreen as FullscreenIcon, FullscreenExit as FullscreenExitIcon,
  EmojiEvents as EmojiEventsIcon, ZoomOutMap as ZoomOutMapIcon } from '@mui/icons-material'
import { common as commonColors, yellow } from '@mui/material/colors'

import { useResizeDetector } from 'react-resize-detector'
import SplitPane from 'react-split-pane'
import { useHotkeys } from 'react-hotkeys-hook'
import './resizer.css'

import { Map as OLMap, View } from 'ol'
import TileLayer from 'ol/layer/Tile'
import GroupLayer from 'ol/layer/Group'
import WebGLTileLayer from 'ol/layer/WebGLTile.js'
import 'ol/ol.css'
import IIIF from 'ol/source/IIIF'
import IIIFInfo from 'ol/format/IIIFInfo'
import {OverviewMap, ScaleLine} from 'ol/control'
import './map.css'
import Projection from 'ol/proj/Projection'
import { Polygon, Point } from "ol/geom"
import { fromExtent } from "ol/geom/Polygon"
import Feature from "ol/Feature"
import {getCenter} from 'ol/extent'
import { Style, Stroke, Fill, Circle } from 'ol/style'
import VectorSource from 'ol/source/Vector'
import VectorLayer from 'ol/layer/Vector'
import Select from 'ol/interaction/Select'

import Progress from '../Progress'
import StackSelector from './StackSelector'
import LabelsSwitch from './LabelsSwitch'
import SelectedFeatures from './SelectedFeatures'
import SlideFeaturesFiltersSwitch from './SlideFeaturesFiltersSwitch'
import { GetSlideMetadata, GetIIIFjson, GetAnalysis } from '../../utils/apiManager'
import { ndp2iiifUrl, ndp2iiifLogin, ndp2iiifPassword, openLayersAnimateDuration } from '../../utils/config'

const gammaMarks = [
  {
    value: 1.0
  }
]

export default function Viewer() {
  const theme = useTheme()
  const [slides, activeSlide, validatedSlide, diagnosesValues, validateSlide, previousSlide, nextSlide, goToSlide, goToResultList, slowMotion] = useOutletContext()
  const [map, setMap] = React.useState()
  const [IIIFLayer, setIIIFLayer] = React.useState()
  const [slideMetadata, setSlideMetadata] = React.useState()
  const [activeStack, setActiveStack] = React.useState()
  const [zoomLevel, setZoomLevel] = React.useState()
  const [loading, setLoading] = React.useState(false)
  const [zoomList, setZoomList] = React.useState()
  const [openSnackbar, setOpenSnackbar] = React.useState(false)
  const [snackbarMessage, setSnackbarMessage] = React.useState('')
  const [snackbarSeverity, setSnackBarSeverity] = React.useState(null)
  const [gammaCorrection, setGammaCorrection] = React.useState(1.0)
  const [diagnosisValue, setDiagnosisValue] = React.useState(null)
  const [analysis, setAnalysis] = React.useState(null)
  const [mainSlideFeatureFilters, setMainSlideFeatureFilters] = React.useState()
  const [mainActiveFeatureFilters, setMainActiveFeatureFilters] = React.useState()
  const [mainActiveLabels, setMainActiveLabels] = React.useState({})
  const [activeLabels, setActiveLabels] = React.useState({})
  const [customTheme, setCustomTheme] = React.useState()
  const [mainCustomTheme, setMainCustomTheme] = React.useState()
  const [mainCustomPastelTheme, setMainCustomPastelTheme] = React.useState()
  const mainLabels = React.useRef()
  const mainStyles = React.useRef({})
  const mainHoverStyles = React.useRef({})
  const [mainFeatures, setMainFeatures] = React.useState(null)
  const [mainSimpleFeatures, setMainSimpleFeatures] = React.useState(null)
  const [mainPointFeatures, setMainPointFeatures] = React.useState(null)
  const activeFeatureFiltersRef = React.useRef([])
  const slideFeatureFiltersRef = React.useRef([])
  const activeLabelsRef = React.useRef([])
  const labels = React.useRef([])
  const styles = React.useRef([])
  const hoverStyles = React.useRef([])
  const [selectedFeatures, setSelectedFeatures] = React.useState([])
  const [assetsNumberByLabel, setAssetsNumberByLabel] = React.useState(new Map())
  const [mainAnalysis, setMainAnalysis] = React.useState(null)
  const [activeAnalysis, setActiveAnalysis] = React.useState(null)
  const [activeFeatureFilters, setActiveFeatureFilters] = React.useState()
  const [assetsNumberByFilter, setAssetsNumberByFilter] = React.useState(new Map())
  const [expanded, setExpanded] = React.useState(false)
  const [slideFeatureFilters, setSlideFeatureFilters] = React.useState()
  const goldenAngle = 180 * (3 - Math.sqrt(5))

  React.useEffect(() => {
    if ((!activeAnalysis || (mainAnalysis && activeAnalysis === mainAnalysis.name)) && mainPointFeatures && map) {
      map.getLayers().forEach(function(layer) {
        if (layer.values_.name === 'pointVectorLayer') {
          layer.getSource().clear()
          layer.getSource().addFeatures(mainPointFeatures)
        }
      })
    }
  }, [map, activeAnalysis, mainPointFeatures])

  React.useEffect(() => {
    if ((!activeAnalysis || (mainAnalysis && activeAnalysis === mainAnalysis.name)) && mainSimpleFeatures && map) {
      map.getLayers().forEach(function(layer) {
        if (layer.values_.name === 'simpleVectorLayer') {
          layer.getSource().clear()
          layer.getSource().addFeatures(mainSimpleFeatures)
        }
      })
    }
  }, [map, activeAnalysis, mainSimpleFeatures])

  React.useEffect(() => {
    if ((!activeAnalysis || (mainAnalysis && activeAnalysis === mainAnalysis.name)) && mainFeatures && map) {
      map.getLayers().forEach(function(layer) {
        if (layer.values_.name === 'vectorLayer') {
          layer.getSource().clear()
          layer.getSource().addFeatures(mainFeatures)
        }
      })
    }
  }, [map, activeAnalysis, mainFeatures])

  React.useEffect(() => {
    if ((!activeAnalysis || (mainAnalysis && activeAnalysis === mainAnalysis.name)) && mainHoverStyles) {
      hoverStyles.current = mainHoverStyles.current
    }
  }, [activeAnalysis, mainHoverStyles.current])

  React.useEffect(() => {
    if ((!activeAnalysis || (mainAnalysis && activeAnalysis === mainAnalysis.name)) && mainStyles) {
      styles.current = mainStyles.current
    }
  }, [activeAnalysis, mainStyles.current])

  React.useEffect(() => {
    if ((!activeAnalysis || (mainAnalysis && activeAnalysis === mainAnalysis.name)) && mainLabels.current) {
      labels.current = mainLabels.current
    }
  }, [activeAnalysis, mainLabels.current])

  React.useEffect(() => {
    if ((!activeAnalysis || (mainAnalysis && activeAnalysis === mainAnalysis.name)) && mainActiveLabels) {
      setActiveLabels(mainActiveLabels)
      activeLabelsRef.current = mainActiveLabels
    }
  }, [activeAnalysis, mainActiveLabels])

  React.useEffect(() => {
    if ((!activeAnalysis || (mainAnalysis && activeAnalysis === mainAnalysis.name)) && mainActiveFeatureFilters) {
      setActiveFeatureFilters(mainActiveFeatureFilters)
      activeFeatureFiltersRef.current = mainActiveFeatureFilters
    }
  }, [activeAnalysis, mainActiveFeatureFilters])

  React.useEffect(() => {
    if ((!activeAnalysis || (mainAnalysis && activeAnalysis === mainAnalysis.name)) && mainSlideFeatureFilters) {
      setSlideFeatureFilters(mainSlideFeatureFilters)
      slideFeatureFiltersRef.current = mainSlideFeatureFilters
    }
  }, [activeAnalysis, mainSlideFeatureFilters])

  React.useEffect(() => {
    if ((!activeAnalysis || (mainAnalysis && activeAnalysis === mainAnalysis.name)) && mainCustomPastelTheme) {
      setMainCustomPastelTheme(mainCustomPastelTheme)
    }
  }, [activeAnalysis, mainCustomPastelTheme])

  React.useEffect(() => {
    if ((!activeAnalysis || (mainAnalysis && activeAnalysis === mainAnalysis.name)) && mainCustomTheme) {
      setCustomTheme(mainCustomTheme)
    }
  }, [activeAnalysis, mainCustomTheme])

  React.useEffect(() => {
    if(map){
      map.getLayers().forEach(function(layer) {
        if (layer.values_.name === 'vectorLayer') {
          let source = layer.getSource()
          slideFeatureFiltersRef.current.forEach(filter => {
            let sum = 0
            source.getFeatures().forEach(feature => {
              if(isFeatureActive(feature) && isFeatureMatchFilter(feature, filter, true)) {
                sum++
              }
            })
            setAssetsNumberByFilter(map => new Map(map.set(filter.name, sum)))
          })
          if(labels.current) {
            labels.current.forEach(label => {
              let sum = 0
              source.getFeatures().forEach(feature => {
                const assetLabel = feature.get('asset')['label']
                if(label === assetLabel) {
                  sum++
                }
              })
              setAssetsNumberByLabel(map => new Map(map.set(label, sum)))
            })
          }
        }
        if (layer instanceof VectorLayer) {
          layer.changed()
        }
      })
    }
  }, [activeLabels, activeFeatureFilters, slideFeatureFilters])
  
  const handleExpandedChange = (panel) => (event, isExpanded) => {
    setExpanded(isExpanded ? panel : false);
  }

  function featureFiltersChanged(filterName, value) {
    let index = slideFeatureFiltersRef.current.findIndex(filter => filter.name === filterName)
    if (slideFeatureFiltersRef.current[index].type === 'float') {
      slideFeatureFiltersRef.current[index].min = value[0]
      slideFeatureFiltersRef.current[index].max = value[1]
    }
    setSlideFeatureFilters({
      ...slideFeatureFilters,
      [index]: slideFeatureFiltersRef.current[index],
    })
  }

  function activeFeatureFiltersChanged(event) {
    setActiveFeatureFilters({
      ...activeFeatureFilters,
      [event.target.name]: event.target.checked,
    })
    activeFeatureFiltersRef.current[event.target.name] = event.target.checked
  }

  function selectFeaturesForLabel (label) {
    map.getLayers().forEach(function(layer) {
      if (layer.values_.name === 'vectorLayer') {
        map.getInteractions().forEach((interaction) => {
          if (interaction instanceof Select) {
            let features = layer.getSource().getFeatures().filter(feature => isFeatureActive(feature) && feature.get('asset')['label'] === label)
            interaction.getFeatures().clear()
            features.forEach(feature => {
              interaction.getFeatures().push(feature)
            })
            interaction.dispatchEvent({
               type: 'select',
               selected: features,
               deselected: []
            })
          }
        })
      }
    })
  }

  function centerOnFeature(feature) {
    const polygon = feature.getGeometry()
    map.getView().fit(polygon, {padding: [30, 30, 30, 30], duration: slowMotion ? openLayersAnimateDuration : 0})
  }

  function centerOnRandomFeatureForLabel (label) {
    map.getLayers().forEach(function(layer) {
      if (layer.values_.name === 'vectorLayer') {
        let features = layer.getSource().getFeatures().filter(feature => isFeatureActive(feature) && feature.get('asset')['label'] === label)
        let feature = features[Math.floor(Math.random()*features.length)]
        if(feature) {
          centerOnFeature(feature)
        }
      }
    })
  }

  function centerOnRandomFeatureForFilter (filter) {
    map.getLayers().forEach(function(layer) {
      if (layer.values_.name === 'vectorLayer') {
        let features = layer.getSource().getFeatures().filter(feature => isFeatureActive(feature) && isFeatureMatchFilter(feature, filter, true))
        let feature = features[Math.floor(Math.random()*features.length)]
        if(feature) {
          centerOnFeature(feature)
        }
      }
    })
  }

  function selectFeaturesForFilter (filter) {
    map.getLayers().forEach(function(layer) {
      if (layer.values_.name === 'vectorLayer') {
        map.getInteractions().forEach((interaction) => {
          if (interaction instanceof Select) {
            let features = layer.getSource().getFeatures().filter(feature => isFeatureActive(feature) && isFeatureMatchFilter(feature, filter, true))
            interaction.getFeatures().clear()
            features.forEach(feature => {
              interaction.getFeatures().push(feature)
            })
            interaction.dispatchEvent({
               type: 'select',
               selected: features,
               deselected: []
            })
          }
        })
      }
    })
  }

  function activeLabelsChanged(event) {
    setActiveLabels({
      ...activeLabels,
      [event.target.name]: event.target.checked,
    })
    activeLabelsRef.current[event.target.name] = event.target.checked
  }

  function createStyle (color, widthModificator, zIndex, lineDash, lineDashOffset) {
    return new Style({
      image: new Circle({
        fill: new Fill({
          color: color,
        }),
        stroke: new Stroke({
          color: color,
          width: 1 * widthModificator,
        }),
        radius: 1,
      }),
      stroke: new Stroke({
        color: color,
        width: 2 * widthModificator,
        lineDash: lineDash,
        lineDashOffset: lineDashOffset,
      }),
      zIndex: zIndex
    })
  }

  const emptyStyle = new Style({})
  const defaultStyle = createStyle('#000', 1, 1)
  const defaultHoverStyle = createStyle('#000', 3, 3)
  const selectedStyle = createStyle('rgba(0, 255, 255, 0.7)', 1, 4)

  function isFeatureMatchFilter(feature, filter, force_contains) {
    const assetFeatures = feature.get('asset')['features']

    let match = false
    if (!activeFeatureFiltersRef.current[filter.name]) {
      match = true
    } else if (activeFeatureFiltersRef.current[filter.name]) {
      if (Object.keys(assetFeatures).indexOf(filter.name) !== -1) {
        if (filter.type === 'float' && assetFeatures[filter.name] >= filter.min && assetFeatures[filter.name] <= filter.max) {
          match = true
        }
      } else if (!force_contains && Object.keys(assetFeatures).indexOf(filter.name) === -1) {
        match = true
      }
    }

    return match
  }

  function isFeatureMatchFilters(feature) {
    return slideFeatureFiltersRef.current.every(filter =>
      isFeatureMatchFilter(feature, filter)
    )
  }

  function isFeatureActive(feature) {
    const origin = feature.get('origin')
    const label = feature.get('asset')['label']
    
    return isFeatureMatchFilters(feature) && activeLabelsRef.current[label] && origin === 'main'
  }

  function genericStyleFunction(feature, stylelist, styleDefault) {
    let style = emptyStyle
    const label = feature.get('asset')['label']
    if(isFeatureActive(feature) && labels.current) {
      const index = labels.current.indexOf(label)
      if(index > -1) {
        style =  stylelist[index]
      } else {
        style = styleDefault
      }
    }
    return style
  }
  function styleFunction(feature) {
    return genericStyleFunction(feature, styles.current, defaultStyle)
  }
  function hoveredStyle(feature) {
    return genericStyleFunction(feature, hoverStyles.current, defaultHoverStyle)
  }

  const handleDiagnosisValueChange = (event) => {
    setDiagnosisValue((event.target).value)
  };

  const handleCloseSnackbar = (event, reason) => {
    setOpenSnackbar(false)
  }

  useHotkeys('shift+up', () => setActiveStackInternal(activeStack-1), [activeStack, map])
  useHotkeys('shift+down', () => setActiveStackInternal(activeStack+1), [activeStack, map])
  useHotkeys('f11', event => toggleFullscreen(event))

  const { ref, width } = useResizeDetector()
  let tempSplit = 600
  if(localStorage.getItem('splitPos')) {
    tempSplit = localStorage.getItem('splitPos')
  }
  const [splitSize, setSplitSize] = React.useState(parseInt(tempSplit))
  const mapElement = React.useRef()
  const mapRef = React.useRef()
  mapRef.current = map

  function renderZoomLevel () {
    if(slideMetadata) {
      var newZoom = (slideMetadata.zoom * 2)*2**((map.getView().getZoom() - map.getView().getMaxZoom())/2)
      if (zoomLevel !== newZoom) {
        setZoomLevel(Math.round(newZoom * 10) / 10)
      }
    }
  }

  function setZoomLevelInternal (e) {
    map.getView().setZoom((2+e.target.value)*2)
    e.target.value = ''
  }

  React.useEffect(() => {
    if(map) {
      renderZoomLevel()
      map.getView().on('change:resolution', renderZoomLevel)
    }
  }, [map])

  React.useEffect(() => {
    if(slides.length > 0) {
      if((validatedSlide > activeSlide || validatedSlide === slides.length-1)) {
        if(map) {
          map.getLayers().forEach(function(layer) {
            map.removeLayer(layer)
          })
        }
        GetAnalysis(slides[activeSlide].analysis_file_name).then(function(myJson) {
          myJson = JSON.parse(myJson)
          console.log(myJson)
          setAnalysis(myJson)
          fetchData(-1, myJson)
        }).catch(e => {
          console.log('Could not get analysis ' + e)
        })
      } else {
        fetchData(-1)
      }
    }
  }, [slides, activeSlide])

  function hslToHex(h, s, l) {
    l /= 100
    const a = s * Math.min(l, 1 - l) / 100
    const f = n => {
      const k = (n + h / 30) % 12
      const color = l - a * Math.max(Math.min(k - 3, 9 - k, 1), -1)
      return Math.round(255 * color).toString(16).padStart(2, '0')
    }
    return `#${f(0)}${f(8)}${f(4)}`
  }

  async function fetchData (stack, analysis) {
    let old_zoom = -1
    let old_center = -1
    if(map) {
      if(stack !== -1) {
        old_zoom = map.getView().getZoom()
        old_center = map.getView().getCenter()
      }
    }

    let imageInfo
    let iiifTileSource
    if(stack === -1) {
      let tempSlideMetadata
      try {
        tempSlideMetadata = await GetSlideMetadata(slides[activeSlide].slide_name)
      } catch (e) {
        console.log(e)
        setSnackbarMessage('Could not retrieve slide metadata: File not found, the slide must have been previously added to the VitaDX directory')
        setSnackBarSeverity('error')
        setOpenSnackbar(true)
        setLoading(false)
        return
      }
      console.log(tempSlideMetadata)
      try {
        tempSlideMetadata = JSON.parse(tempSlideMetadata)
      } catch (e) {
        console.log(e)
        setSnackbarMessage('Could not parse slide metadata: ' + e)
        setSnackBarSeverity('error')
        setOpenSnackbar(true)
        setLoading(false)
        return
      }
      console.log(tempSlideMetadata)
      tempSlideMetadata.zstacks.forEach((stack, index) => {
        if (stack % 1000 === 0) {
          tempSlideMetadata.zstacks[index] = stack / 1000 + 'μ'
        }
      })
      setSlideMetadata(tempSlideMetadata)
      stack = Math.floor((tempSlideMetadata['zstacks'].length - 1) / 2)
      var zoom_list = []
      for (let scaling = -6; scaling < 1; scaling++){
          zoom_list.push(Math.round(100*2**scaling*tempSlideMetadata.zoom*2)/100);
      }
      setZoomList(zoom_list)
      console.log(zoom_list)
      setActiveStack(stack)
    }

    try {
      imageInfo = await GetIIIFjson(slides[activeSlide].slide_name, stack)
    } catch (e) {
      setSnackbarMessage('Could not retrieve IIIF JSON: ' + e)
      setSnackBarSeverity('error')
      setOpenSnackbar(true)
      setLoading(false)
      return
    }
    if (!imageInfo['@id'].includes('http://')) {
      imageInfo['@id'] = 'http://' + imageInfo['@id']
    }
    imageInfo['@id'] = imageInfo['@id'].replace('127.0.0.1:5002', 'server-algo.local:9999')
    console.log(imageInfo)
    const options = new IIIFInfo(imageInfo).getTileSourceOptions()
    if (options === undefined || options.version === undefined) {
      console.log('Data seems to be no valid IIIF image information.')
      return
    }
    options.zDirection = -1
    options.crossOrigin = 'anonymous'
    iiifTileSource = new IIIF(options)
    iiifTileSource.setTileLoadFunction(function (tile, src) {
      const xhr = new XMLHttpRequest()
      xhr.responseType = 'blob'
      xhr.addEventListener('loadend', function (evt) {
        const data = this.response
        if (data) {
          tile.getImage().src = URL.createObjectURL(data)
        } else {
          tile.setState(3)
        }
      })
      xhr.addEventListener('error', function () {
        tile.setState(3)
      })

      if(!src.includes('http://') && !src.includes('https://')) {
        src = 'http://' + src
      }
      const url = new URL(src)
      src = ndp2iiifUrl + "/ndp2iiif" + url.pathname
      xhr.open('GET', src)
      xhr.setRequestHeader("Authorization", "Basic " + btoa(ndp2iiifLogin + ":" + ndp2iiifPassword))
      xhr.send()
    })
    
    let resolutions = [128]
    let temp = Math.sqrt(2)
    while(resolutions[resolutions.length-1] > 0.5){
      resolutions.push(resolutions[resolutions.length-1]/temp)
    }

    const source = new VectorSource({
      features: []
    })
    const simpleSource = new VectorSource({
      features: []
    })
    const pointSource = new VectorSource({
      features: []
    })
    const nucleusSource = new VectorSource({features: []})

    const vectorLayer = new VectorLayer({
      minZoom: 3,
      source: source,
      style: styleFunction,
      name: 'vectorLayer',
    })
    const simpleVectorLayer = new VectorLayer({
      maxZoom: 3,
      minZoom: 1,
      source: simpleSource,
      style: styleFunction,
      name: 'simpleVectorLayer',
    })
    const pointVectorLayer = new VectorLayer({
      maxZoom: 1,
      source: pointSource,
      style: styleFunction,
      name: 'pointVectorLayer',
    })
    const nucleusVectorLayer = new VectorLayer({
      source: nucleusSource,
      style: selectedStyle,
      name: 'nucleusVectorLayer',
    })

    const imageLayer = new WebGLTileLayer({
      style: {
        exposure: ['var', 'gamma'],
        variables: {
          gamma: gammaCorrection - 1
        },
      },
      preload: Infinity
    })
    imageLayer.setSource(iiifTileSource)
    
    let initialMap
    if(map) {
      initialMap = map
      initialMap.getLayers().forEach(function(layer) {
        initialMap.removeLayer(layer)
       })
      initialMap.getControls().forEach(function(control) {
        if (control instanceof OverviewMap)
          initialMap.removeControl(control)
      })
      initialMap.addLayer(imageLayer)
      initialMap.addLayer(vectorLayer)
      initialMap.addLayer(simpleVectorLayer)
      initialMap.addLayer(pointVectorLayer)
      initialMap.addLayer(nucleusVectorLayer)
    } else {
      initialMap = new OLMap({
        layers: [imageLayer, vectorLayer, simpleVectorLayer, pointVectorLayer, nucleusVectorLayer],
        target: mapElement.current,
      })
  
      const scaleLine = new ScaleLine({
        units: 'metric',
      })
      initialMap.addControl(scaleLine)
    }

    initialMap.setView(
      new View({
        projection: new Projection({
          units: 'm',
          metersPerUnit: 1/4524886,
        }),
        resolutions: resolutions, // [128, 64, 32, 16, 8, 4, 2, 1, 0.5]
        constrainResolution: true,
        extent: iiifTileSource.getTileGrid().getExtent(), // [0, -height, width, 0] -> [minx, miny, maxx, maxy]
        constrainOnlyCenter: true,
      })
    )
    initialMap.getView().fit(iiifTileSource.getTileGrid().getExtent())
    if(old_zoom !== -1) {
      initialMap.getView().setCenter(old_center)
      initialMap.getView().setZoom(old_zoom)
    }

    const overviewLayer = new TileLayer()
    const overviewMapControlTemp = new OverviewMap({
      className: 'ol-overviewmap ol-custom-overviewmap',
      collapseLabel: '\u00AB',
      label: '\u00BB',
      collapsed: false,
      view: new View({
        extent: iiifTileSource.getTileGrid().getExtent(), // [0, -height, width, 0] -> [minx, miny, maxx, maxy]
       }),
    })
    overviewLayer.setSource(iiifTileSource)
    initialMap.addControl(overviewMapControlTemp)
    var overviewMap = overviewMapControlTemp.getOverviewMap()
    const overviewMapControl = new OverviewMap({
      className: 'ol-overviewmap ol-custom-overviewmap',
      collapseLabel: '\u00AB',
      label: '\u00BB',
      collapsed: false,
      view: new View({
        extent: iiifTileSource.getTileGrid().getExtent(), // [0, -height, width, 0] -> [minx, miny, maxx, maxy]
        resolutions: [overviewMap.getView().values_.resolution]
      }),
    })
    overviewLayer.setSource(iiifTileSource)
    overviewMapControlTemp.setMap(null)
    initialMap.addControl(overviewMapControl)
    var overviewMap = overviewMapControl.getOverviewMap()
    overviewMap.setLayerGroup(new GroupLayer())
    overviewMap.addLayer(overviewLayer)
  
    iiifTileSource.once(['tileloadend', 'imageloadend'], function (event) {
      overviewMap.render()
    })

    if(analysis && analysis['assets']){
      let features = []
      let simpleFeatures = []
      let pointFeatures = []
      let tempLabels = new Set()
      let tempSlideFeatures = new Set()
      let filters = []
      let tempActiveSlideFeaturesFilters = {}
      let assets = analysis['assets'].filter(asset => asset['mask'] && asset['bounding_box'])
      features = new Array(assets.length)
      let defaultSlideFeaturesFilters = analysis['features_filters']
      assets.forEach((asset, index) => {
        const mask = asset['mask']
        let coordinates = new Array(mask.length)
        mask.forEach((coordinate, index) => {
          coordinates[index] = [coordinate[1], -coordinate[0]]
        })
        asset['label'] = asset['label'] ? asset['label'] : ''
        asset['nucleus'] = asset['nucleus'] ? asset['nucleus'] : {'mask' : []}
        asset['nucleus']['mask'] = asset['nucleus']['mask'] ? asset['nucleus']['mask'] : []
        asset['features'] = asset['features'] ? asset['features'] : []
        const geometry = new Polygon([coordinates])
        features[index] = new Feature({
          geometry: geometry,
          asset: asset,
          origin: 'main'
        })
        features[index].setId(index)
        const boundingBox = asset['bounding_box']
        const extent = [boundingBox[1], -boundingBox[0], boundingBox[3], -boundingBox[2]]
        const simpleGeometry = new fromExtent(extent)
        simpleFeatures[index] = new Feature({
          geometry: simpleGeometry,
          asset: asset,
          origin: 'main'
        })
        simpleFeatures[index].setId(index)
        tempLabels.add(asset['label'])
        Object.keys(asset['features']).map(key => {
          tempSlideFeatures.add(key)
          defaultSlideFeaturesFilters.forEach(filter => {
            filter.assetNumber = filter.assetNumber + 1 || 1
            if(key === filter.name && filter.type === 'float') {
              if (!filter.sliderMin || filter.sliderMin > asset['features'][key]) {
                filter.sliderMin = asset['features'][key]
              }
              if (!filter.sliderMax || filter.sliderMax < asset['features'][key]) {
                filter.sliderMax = asset['features'][key]
              }
            }
          })
        })
        var center = getCenter(extent)
        pointFeatures[index] = new Feature({
          geometry: new Point(center),
          asset: asset,
          origin: 'main'
        })
        pointFeatures[index].setId(index)
      })
      defaultSlideFeaturesFilters.forEach(filter => {
        if(tempSlideFeatures.has(filter.name)) {
          tempActiveSlideFeaturesFilters[filter.name] = true
          if(filter.type === 'float') {
            filter.marks = [
              {
                value: Math.floor(filter.sliderMin * 100) / 100,
                label: Math.floor(filter.sliderMin * 100) / 100,
              }
            ]
            if (!filter.min || filter.min > filter.max || filter.min > filter.sliderMax || filter.min < filter.sliderMin) {
              filter.min = Math.floor(filter.sliderMin * 100) / 100
            } else {
              filter.marks.push(
                {
                  value: Math.floor(filter.min * 100) / 100,
                  label: Math.floor(filter.min * 100) / 100,
                }
              )
            }
            if (!filter.max || filter.max < filter.min || filter.max > filter.sliderMax) {
              filter.max = Math.ceil(filter.sliderMax * 100) / 100
            } else {
              filter.marks.push(
                {
                  value: Math.ceil(filter.max * 100) / 100,
                  label: Math.ceil(filter.max * 100) / 100,
                }
              )
            }
            filter.marks.push(
              {
                value: Math.ceil(filter.sliderMax * 100) / 100,
                label: Math.ceil(filter.sliderMax * 100) / 100,
              }
            )
            filter.step = 1
            let diff = filter.sliderMax - filter.sliderMin
            if (diff > 10) {
              filter.step = 1 * Math.pow(10, diff.toString().length-2)
            } else if (diff !== 0) {
              var log10 = Math.log10(diff)
              if (log10 + 1 < 0) {
                log10 = (log10*-1)-1
              }
              var zeroDecimals = Math.floor(log10 + 1)
              filter.step = 1 / Math.pow(10, zeroDecimals+2)
            }
          }
          filters.push(filter)
        }
      })
      console.log(filters)

      tempLabels = Array.from(tempLabels)
      tempLabels.sort()
      console.log(tempLabels)

      let tempStyles = []
      let tempHoverStyles = []
      let labelList = []
      let tempActiveLabels = {}
      let palette = {}
      let pastelPalette = {}
      tempLabels.forEach((label, index) => {
        let color = hslToHex(index * goldenAngle, 100, 50)
        let pastelColor = hslToHex(index * goldenAngle, 100, 75)

        if (!analysis['labels'] || analysis['labels'].indexOf(label) !== -1) {
          tempActiveLabels[label] = true
          labelList.push(label)
          palette[label] = {
            main: color
          }
          pastelPalette[label] = {
            main: pastelColor
          }
          tempStyles.push(createStyle(color, 1, 1))
          tempHoverStyles.push(createStyle(color, 2, 3))
        }
      })

      console.log(filters)
      setMainSlideFeatureFilters(filters)
      setMainActiveFeatureFilters(tempActiveSlideFeaturesFilters)
      setMainActiveLabels(tempActiveLabels)
      setMainCustomTheme(createTheme({ palette: palette }))
      setMainCustomPastelTheme(createTheme({ palette: pastelPalette }))
    
      mainLabels.current = labelList
      mainStyles.current = tempStyles
      mainHoverStyles.current = tempHoverStyles

      setMainFeatures(features)
      setMainSimpleFeatures(simpleFeatures)
      setMainPointFeatures(pointFeatures)

      const selectSingleClick = new Select({style: selectedStyle, layers: [vectorLayer, simpleVectorLayer, pointVectorLayer], hitTolerance: 3})
      initialMap.addInteraction(selectSingleClick)
      let selected = null
      selectSingleClick.on('select', function (e) {
        let array = []
        if(e.target) {
          e.target.getFeatures().array_.forEach(f => {
            array.push(f)
          })
        } else {
          e.selected.forEach(f => {
            array.push(f)
          })
        }
        setSelectedFeatures(array)
        selected = array
      })

      let hovered = new Map()
      initialMap.on('pointermove', function (e) {
        var toDelete = []
        hovered.forEach((f, id) => {
          if(selected === null || !selected.some(feature => feature.getId() === id)) {
            f.setStyle(undefined)
            toDelete.push(id)
          }
        })
        toDelete.forEach(id => {
          hovered.delete(id)
        })
      
        initialMap.forEachFeatureAtPixel(e.pixel, function (f) {
          if(f.get('asset') && f.get('origin') === 'main' && (selected === null || !selected.some(feature => feature.getId() === f.getId()))) {
            hovered.set(f.getId(), f)
            f.setStyle(hoveredStyle)
          }
        }, {hitTolerance: 3})
      })
    } else if (map) {
      map.getLayers().forEach(function(layer) {
        if (layer instanceof TileLayer) {
          layer.setSource(iiifTileSource)
        }
      })
      map.getControls().forEach(function(control) {
        if (control instanceof OverviewMap) {
          const overviewMap = control.getOverviewMap()
          overviewMap.values_.layergroup.values_.layers.forEach(layer => {
            layer.setSource(iiifTileSource)
          })
        }
      })
    }

    const scaleLine = new ScaleLine({
      units: 'metric',
    })
    initialMap.addControl(scaleLine)

    if(map) {
      renderZoomLevel()
      map.getView().on('change:resolution', renderZoomLevel)
    } else {
      setMap(initialMap)
    }
    setIIIFLayer(imageLayer)

    setLoading(false)
  }
  
  function onSplitSizeChange(size) {
    setSplitSize(size)
    localStorage.setItem('splitPos', size)
  }

  React.useEffect(() => {
    map && setTimeout( function() { map.updateSize()}, 200)
  }, [width])

  function setActiveStackInternal(value) {
    if(value >= 0 && slideMetadata && value < slideMetadata.zstacks.length) {
      setActiveStack(value)
      fetchData(value, analysis)
    }
  }

  function getFullscreenElement() {
     return document.fullscreenElement   //standard property
     || document.webkitFullscreenElement //safari/opera support
     || document.mozFullscreenElement    //firefox support
     || document.msFullscreenElement     //ie/edge support
  }

  function toggleFullscreen (e) {
    if(e) {
      e.preventDefault()
    }
    if (getFullscreenElement()) {
      document.exitFullscreen()
    } else {
      document.documentElement.requestFullscreen().catch(console.log)
    }
  }

  function zoomOutMap () {
    console.log(map.getView())
    map.getView().fit(map.getView().values_.extent, {duration: slowMotion ? openLayersAnimateDuration : 0})
  }

  const handleGammaCorrectionChange = (event, newValue) => {
    setGammaCorrection(newValue)
    const updates = {'gamma': newValue - 1}
    IIIFLayer.updateStyleVariables(updates)
  }

  const validateSlideInternal = () => {
    validateSlide(diagnosisValue)
    setDiagnosisValue(null)
  }

  function diagnosis_to_visiocyt(diagnosis) {
    let returnValue = "Positif"
    if (diagnosis === "C1" || diagnosis === "C2" || diagnosis === "C3") {
      returnValue = "Négatif"
    }
    return returnValue
  }

  function are_visiocyts_equals(v1, v2) {
    return v1.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '') === v2.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '')
  }

  return (
    <SplitPane split="vertical" minSize={400} maxSize={500}
      defaultSize={splitSize}
      onDragFinished={onSplitSizeChange}
      style={{ position: 'unset' }}
      resizerStyle={{ ...(theme.palette.mode === 'dark' ? 
        {
          width: '11px',
          background: '#fff',
          borderLeft: '5px solid rgba(0, 0, 0, 1)',
          borderRight: '5px solid rgba(0, 0, 0, 1)',
        }
          : 
        { 
          width: '11px',
          background: '#000',
          borderLeft: '5px solid rgba(255, 255, 255, 1)',
          borderRight: '5px solid rgba(255, 255, 255, 1)',
        }) 
      }}
    >
      <Box marginBottom='1vh' style={{ paddingRight: '10px', overflow: 'auto', maxHeight: '99%' }}>
        { slides.length > 0 &&
          <div>
            <div>
              <Card>
                <CardContent>
                  <Typography variant="h5" component="div" sx={{ paddingBottom: '10px' }}>
                    Informations patient
                  </Typography>
                  <div>{slides[activeSlide].patient_datas.gender + ', ' + slides[activeSlide].patient_datas.age + ' ans, ' + slides[activeSlide].patient_datas.smoker_status}</div>
                </CardContent>
              </Card>
            </div>
            <div style={{ paddingTop: '5px' }}>
              <Card>
                {(validatedSlide <= activeSlide && validatedSlide !== slides.length-1) ?
                  <div>
                    <CardContent>
                      <Typography variant="h5" component="div" sx={{ paddingBottom: '10px' }}>
                        Diagnostic
                      </Typography>
                      <RadioGroup
                        row
                        value={diagnosisValue}
                        onChange={handleDiagnosisValueChange}
                      >
                        {['C1', 'C2', 'C3', 'C4', 'C5', 'C6'].map(value => (
                          <FormControlLabel value={value} control={<Radio />} label={value} key={value}/>
                        ))}
                      </RadioGroup>
                      <div style={{ paddingTop: '10px' }}>
                        <Button variant="outlined" onClick={validateSlideInternal} disabled={!diagnosisValue}>Valider</Button>
                      </div>
                    </CardContent>
                  </div>
                  :
                  <div>
                    <CardContent>
                      <Typography variant="h5" component="div" sx={{ display: 'flex', justifyContent: 'space-between', paddingBottom: '10px' }}>
                        {"Votre diagnostic: " + diagnosesValues[activeSlide]}
                        {are_visiocyts_equals(slides[activeSlide].diagnostic_datas.visiocyt, diagnosis_to_visiocyt(diagnosesValues[activeSlide])) &&
                          <EmojiEventsIcon sx={{ color: yellow[700] }} />
                        }
                      </Typography>
                      <div>VisioCyt: {slides[activeSlide].diagnostic_datas.visiocyt}</div>
                      <div>Endoscopie: {slides[activeSlide].diagnostic_datas.endoscopie}</div>
                      <div style={{ paddingTop: '20px', fontSize: '13px' }}>
                        <div>Éléments biologiques détectés : {slides[activeSlide].slide_datas.biological}</div>
                        <div style={{ paddingLeft: '10px'}}>
                          <div>Cellule(s) superficielle(s) : {slides[activeSlide].slide_datas.superficial_cells}</div>
                          <div>Cellule(s) urothéliale(s) : {slides[activeSlide].slide_datas.urothelial_cells}</div>
                          <div style={{ paddingLeft: '10px'}}>
                            <div>Dont cellule(s) avec un RNC supérieur à 0.7 : {slides[activeSlide].slide_datas.cell_nrc}</div>
                          </div>
                        </div>
                        <div>Amas cellulaire(s) : {slides[activeSlide].slide_datas.clusters}</div>
                        <div style={{ paddingLeft: '10px'}}>
                          <div>Dont atypique(s) : {slides[activeSlide].slide_datas.atypical_clusters}</div>
                        </div>
                      </div>
                    </CardContent>
                  </div>
                }
              </Card>
            </div>
            <div style={{ paddingTop: '5px' }}>
              { (validatedSlide > activeSlide || validatedSlide === slides.length-1) &&
                <div>
                  { customTheme &&
                    <LabelsSwitch customTheme={customTheme} activeLabels={activeLabels} activeLabelsChanged={activeLabelsChanged}
                    labels={labels} assetsNumberByLabel={assetsNumberByLabel} centerOnRandomFeatureForLabel={centerOnRandomFeatureForLabel}
                    selectFeaturesForLabel={selectFeaturesForLabel} />
                  }
                  { slideFeatureFiltersRef.current && slideFeatureFiltersRef.current.length > 0 &&
                    <div style={{ paddingTop: '5px'}}>
                      <SlideFeaturesFiltersSwitch activeFeatureFilters={activeFeatureFilters} slideFeatureFilters={slideFeatureFiltersRef}
                      activeFeatureFiltersChanged={activeFeatureFiltersChanged} featureFiltersChanged={featureFiltersChanged} assetsNumberByFilter={assetsNumberByFilter}
                      centerOnRandomFeatureForFilter={centerOnRandomFeatureForFilter} selectFeaturesForFilter={selectFeaturesForFilter}/>
                    </div>
                  }
                  <div style={{ paddingTop: '5px'}}>
                    <SelectedFeatures centerOnFeature={centerOnFeature}
                    features={selectedFeatures}
                    expanded={expanded} handleChange={handleExpandedChange}/>
                  </div>
                  <div style={{ paddingTop: '10px', display: 'flex', justifyContent: 'space-between'}}>
                    <Button variant="outlined" onClick={previousSlide} disabled={activeSlide === 0 || loading}>Précédent</Button>
                    <Button variant="outlined" onClick={nextSlide} disabled={activeSlide === slides.length - 1 || loading}>Suivant</Button>
                    <Button variant="outlined" onClick={goToResultList}>Résultats</Button>
                  </div>
                </div>
              }
            </div>
          </div>
        }
        { loading && <Progress/> }
        <Snackbar
          open={openSnackbar}
          autoHideDuration={6000}
          onClose={handleCloseSnackbar}>
          <Alert  variant='filled' onClose={handleCloseSnackbar} severity={snackbarSeverity} sx={{ width: '100%' }}>
            {snackbarMessage}
          </Alert>         
        </Snackbar>
      </Box>
      <div ref={ref} style={{ height:'100%' }}>
        <div ref={mapElement} className="map-container" id="map-container" style={{ height:'100%' }}>
          { map &&
            <div data-testid='map-controls'>
              <div className='map-zoom-bg'>
                <Tooltip title="Zoom" placement="left">
                  <FormControl size="small" className='map-zoom'>
                    <MuiSelect
                      displayEmpty
                      renderValue={() => zoomLevel}
                      value={''}
                      onChange={(e) => setZoomLevelInternal(e)}
                      sx={{
                        color: commonColors.white
                      }}
                    >
                      {zoomList && zoomList.map((zoom, index) => (    
                        <MenuItem value={index} key={index}>
                          {zoom}
                        </MenuItem>
                      ))}
                    </MuiSelect>
                  </FormControl>
                </Tooltip>
              </div>
              { slideMetadata && 
                <div className='map-stacks-bg'>
                    <StackSelector setActiveStackInternal={setActiveStackInternal} slideMetadata={slideMetadata}
                    activeStack={activeStack}/>
                </div>
              }
              <div className='map-fullscreen-bg'>
                <Tooltip title={ getFullscreenElement() ? "Exit fullscreen" : "Fullscreen"}>
                  <IconButton className='map-fullscreen' size="small" onClick={() => toggleFullscreen()}
                    sx={{
                      borderRadius: '0px',
                      color: commonColors.white
                    }}>
                    { getFullscreenElement() ? <FullscreenExitIcon fontSize="inherit"/> : <FullscreenIcon fontSize="inherit" />}
                  </IconButton>
                </Tooltip>
              </div>
              <div className='map-zoom-out-bg'>
                <Tooltip title="Zoom out map">
                  <IconButton className='map-zoom-out' size="small" onClick={() => zoomOutMap()}
                    sx={{
                      borderRadius: '0px',
                      color: commonColors.white
                    }}>
                    <ZoomOutMapIcon fontSize="inherit"/>
                  </IconButton>
                </Tooltip>
              </div>
              <div className='map-gamma-bg' data-testid='map-gamma'>
                <div className='map-gamma'>Gamma</div>
              </div>
              <div className='map-gamma-slider-bg' data-testid='map-gamma-slider'>
                <Slider
                  sx={{
                    color: 'rgba(0,60,136)',
                  }}
                  orientation="vertical"
                  value={gammaCorrection}
                  onChange={handleGammaCorrectionChange}
                  step={0.1}
                  min={0.5}
                  max={3.5}
                  marks={gammaMarks}
                  valueLabelDisplay="auto"
                />
              </div>
            </div>
          }
        </div>
      </div>
    </SplitPane>
  )
}
